// SFX/Music bed audio routing — shared per-track state used by every Web
// Audio preview path (TTS Timeline, STS master timeline). Owns one
// persistent GainNode + AudioBufferSourceNode pair per track index so
// multiple previews can drive the same beds without re-allocating, and
// exposes per-track "apply gain now" entry points that the mixer popover
// and the duck hook both call into.
//
// Tracks: index 0 → SFX Track 1, index 1 → SFX Track 2 (both optional).
// Up to 2 tracks total — the session store enforces the cap on the data
// side; this module just keeps a Map so callers can pass any track index
// they like and we'll lazily allocate routing for it.
//
// Audio path: bedSource → bedGain → ac.destination (one chain per track).
// We deliberately do NOT route through voiceGain (voice mute would silence
// the bed) nor through the Safari MediaStream tap (voice-only).
//
// Lifecycle:
//   - `scheduleBed(ac, buffer, placement, playheadFrom, atContextTime, track)`
//     stops any previous bedSource for that track, computes the schedule from
//     the BedPlacement + playhead, creates a fresh source, wires it to the
//     track's gain, and starts it.
//   - `stopBedSource(track?)` tears the source down without touching gain.
//     Pass `undefined` to stop all tracks.
//   - `invalidateBedRouting()` drops gains + decoded caches for every track
//     (call on AudioContext close so the next play() rebuilds against a
//     fresh ac).
//   - `applyBedGain(ac, ducked, track)` recomputes the gain using
//     effectiveSfxVolume(state, ducked, track). The ducker calls this for
//     each known track on edge transitions.
//
// External entry points:
//   - `applyBedGainExternalAll(ducked)` pushes the current mixer state to
//     every active track gain — used by the mixer popover/duck hook so a
//     slider/mute change lands on all live tracks at once.

import { effectiveSfxVolume, useMixerStore, type SfxTrackIdx } from '@/stores/mixer';
import { computeBedSchedule, type BedPlacement } from './bedPlacement';

type DecodedCache = { raw: ArrayBuffer; decoded: AudioBuffer; ac: AudioContext };

const bedGains = new Map<SfxTrackIdx, GainNode>();
const bedSources = new Map<SfxTrackIdx, AudioBufferSourceNode>();
const bedDecodedCache = new Map<SfxTrackIdx, DecodedCache>();

const TRACKS: SfxTrackIdx[] = [0, 1];

export function ensureBedGain(ac: AudioContext, track: SfxTrackIdx): GainNode {
  const existing = bedGains.get(track);
  if (existing && existing.context === ac) return existing;
  const g = ac.createGain();
  // Initialise at the current non-ducked SFX gain. Initialising at 0 and
  // relying on the immediately-following setValueAtTime(currentTime) to
  // override it is fragile: both events end up scheduled at the same
  // context-time, and on audio-only assets (where the bed is the ONLY
  // thing routed through this gain) the imperative 0 can win the resolve
  // and silence the bed for the duration of the play. Seeding the value
  // at create-time avoids the race entirely.
  g.gain.value = effectiveSfxVolume(useMixerStore.getState(), false, track);
  g.connect(ac.destination);
  bedGains.set(track, g);
  return g;
}

/** Compute a track's effective gain from the mixer state + duck flag. */
function computeBedGain(ducked: boolean, track: SfxTrackIdx): number {
  return effectiveSfxVolume(useMixerStore.getState(), ducked, track);
}

/** Set a track's bed gain immediately (no ramp) using the current mixer state. */
export function applyBedGain(
  ac: AudioContext,
  ducked: boolean,
  track: SfxTrackIdx,
): void {
  const g = ensureBedGain(ac, track);
  g.gain.setValueAtTime(computeBedGain(ducked, track), ac.currentTime);
}

/** External entry point for callers outside the preview hook (the mixer
 *  popover via useVideoDucking, mostly) to push slider changes to every
 *  active bed gain. No-op for tracks without a gain node yet — the next
 *  play() will build one and apply the current state on its own.
 *
 *  `rampMs` lets the auto-ducker apply attack/release smoothing — `0`
 *  (default) for instant slider changes, >0 for a linear ramp from the
 *  current gain to the target over that many milliseconds. Linear is
 *  perceptually clean over short durations and avoids the asymptote of
 *  setTargetAtTime (which never actually reaches the target value). */
export function applyBedGainExternalAll(ducked: boolean, rampMs = 0): void {
  bedGains.forEach((g, track) => {
    const ac = g.context as AudioContext;
    const target = computeBedGain(ducked, track);
    try {
      if (rampMs <= 0) {
        g.gain.setValueAtTime(target, ac.currentTime);
      } else {
        // Anchor the current value first so the ramp starts from `now` —
        // without the explicit setValueAtTime, linearRampToValueAtTime
        // ramps from the LAST scheduled value (which may be far in the
        // past), producing the wrong shape on rapid duck/unduck cycles.
        g.gain.cancelScheduledValues(ac.currentTime);
        g.gain.setValueAtTime(g.gain.value, ac.currentTime);
        g.gain.linearRampToValueAtTime(target, ac.currentTime + rampMs / 1000);
      }
    } catch {
      /* context closed — next play() will recreate */
    }
  });
}

/** Stop one or all track sources without touching their gains. */
export function stopBedSource(track?: SfxTrackIdx): void {
  const targets: SfxTrackIdx[] = track === undefined ? TRACKS : [track];
  for (const t of targets) {
    const src = bedSources.get(t);
    if (!src) continue;
    try { src.stop(); } catch { /* ignore */ }
    try { src.disconnect(); } catch { /* ignore */ }
    bedSources.delete(t);
  }
}

/** Invalidate everything bound to the previous AudioContext (Safari
 *  recovery + on unmount). Drops gains + decoded caches for every track. */
export function invalidateBedRouting(): void {
  stopBedSource();
  bedGains.clear();
  bedDecodedCache.clear();
}

/** Decode (and cache) a track's raw bytes against the given context. */
export async function decodeBedBuffer(
  ac: AudioContext,
  raw: ArrayBuffer,
  track: SfxTrackIdx,
): Promise<AudioBuffer | null> {
  const cached = bedDecodedCache.get(track);
  if (cached && cached.raw === raw && cached.ac === ac) {
    return cached.decoded;
  }
  try {
    const decoded = await ac.decodeAudioData(raw.slice(0));
    bedDecodedCache.set(track, { raw, decoded, ac });
    return decoded;
  } catch (e) {
    console.warn('[bed] decode failed for track', track, e);
    return null;
  }
}

/** Look up a track's cached decoded buffer — null if cache empty / mismatched. */
export function getCachedBedBuffer(
  ac: AudioContext,
  raw: ArrayBuffer,
  track: SfxTrackIdx,
): AudioBuffer | null {
  const cached = bedDecodedCache.get(track);
  if (cached && cached.raw === raw && cached.ac === ac) return cached.decoded;
  return null;
}

/** Schedule a track's bed for a preview pass, honouring its placement
 *  (place-mode clip offset OR window-mode source window) combined with the
 *  current playhead. `atContextTime` is the shared anchor also used for the
 *  voice clips, so bed + voice fire from the same clock snapshot. Returns
 *  true if a source was started, false if the bed is not audible from
 *  `playheadFrom`.
 *
 *  No gain.setValueAtTime is written here: Safari renders a gain step
 *  scheduled at ac.currentTime against a past src.start(when) as a ~0.5s
 *  ramp instead of an instant step — the rAF duck loop drives gain instead.
 *  A past `when` value is treated by Web Audio as "start immediately", so
 *  the shared anchor is always safe even if the clock has moved past it. */
export function scheduleBed(
  ac: AudioContext,
  buffer: AudioBuffer,
  placement: BedPlacement,
  playheadFrom: number,
  atContextTime: number | undefined,
  track: SfxTrackIdx,
): boolean {
  stopBedSource(track);
  const anchor = atContextTime ?? ac.currentTime;
  const sched = computeBedSchedule(placement, playheadFrom, anchor);
  if (!sched) return false;
  if (sched.offsetIntoBuffer >= buffer.duration) return false;

  const g = ensureBedGain(ac, track);
  const src = ac.createBufferSource();
  src.buffer = buffer;
  src.connect(g);
  bedSources.set(track, src);

  // Clamp the play length to what's actually left in the buffer so a
  // rounding drift between the probed bed.duration and the decoded buffer
  // never asks Web Audio for samples past the end.
  const playLen = Math.min(sched.playLen, buffer.duration - sched.offsetIntoBuffer);
  src.start(sched.when, sched.offsetIntoBuffer, playLen);
  return true;
}
